import React from 'react';
import { ShieldIcon, HomeIcon, HeartPulseIcon, BriefcaseIcon, AlertTriangleIcon, ScaleIcon, PhoneIcon, CheckCircleIcon, StarIcon } from 'lucide-react';
import { FadeIn } from '../components/FadeIn';
import { SectionHeading } from '../components/SectionHeading';
import { PageHero } from '../components/PageHero';
import { CTASection } from '../components/CTASection';
import { Button } from '../components/Button';
interface VerzekeringenPageProps {
  onNavigate: (page: string) => void;
}
export function VerzekeringenPage({
  onNavigate
}: VerzekeringenPageProps) {
  return <div className="w-full" data-id="element-684">
      {/* EMERGENCY BANNER */}
      <div className="bg-navy w-full pt-32 md:pt-36 pb-6 md:pb-8 px-4 border-b-4 border-gold shadow-lg relative z-20" data-id="element-685">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-686">
          <div className="flex items-center text-white" data-id="element-687">
            <div className="w-12 h-12 bg-gold/20 rounded-full flex items-center justify-center mr-4 flex-shrink-0" data-id="element-688">
              <AlertTriangleIcon className="w-6 h-6 text-gold" data-id="element-689" />
            </div>
            <div data-id="element-690">
              <h2 className="text-2xl md:text-3xl font-extrabold text-gold mb-1" data-id="element-691">
                Schade? Wij helpen je direct.
              </h2>
              <p className="text-gray-300" data-id="element-692">
                Geen wachttijden, direct persoonlijk contact.
              </p>
            </div>
          </div>
          <Button variant="primary" size="lg" href="tel:0201234567" icon={<PhoneIcon className="w-5 h-5" data-id="element-694" />} iconPosition="left" className="w-full md:w-auto" data-id="element-693">
            Schade Melden
          </Button>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-warm text-center px-4" data-id="element-695">
        <div className="max-w-4xl mx-auto" data-id="element-696">
          <SectionHeading title="Zekerheid voor wat echt telt" subtitle="Geen onnodige polissen of kleine lettertjes. Ik zorg dat je goed verzekerd bent — niet meer, niet minder. En als er iets gebeurt? Dan bel je mij." data-id="element-697" />
          <FadeIn delay={0.2} data-id="element-698">
            <Button variant="secondary" size="lg" onClick={() => onNavigate('contact')} data-id="element-699">
              Verzekeringen doorspreken
            </Button>
          </FadeIn>
        </div>
      </section>

      {/* Hoe werkt schade melden? */}
      <section className="py-20 md:py-28 bg-white" data-id="element-700">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-701">
          <SectionHeading title="Hoe werkt schade melden?" data-id="element-702" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-703">
            <FadeIn delay={0.1} className="text-center" data-id="element-704">
              <div className="w-16 h-16 bg-navy rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6 shadow-md" data-id="element-705">
                1
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-706">
                Bel ons direct
              </h3>
              <p className="text-gray-600" data-id="element-707">
                Je krijgt direct je eigen adviseur aan de lijn. Geen keuzemenu's
                of wachttijden.
              </p>
            </FadeIn>
            <FadeIn delay={0.2} className="text-center" data-id="element-708">
              <div className="w-16 h-16 bg-navy rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6 shadow-md" data-id="element-709">
                2
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-710">
                Wij regelen het
              </h3>
              <p className="text-gray-600" data-id="element-711">
                Stuur ons foto's via WhatsApp. Ik neem contact op met de
                verzekeraar en regel de afhandeling.
              </p>
            </FadeIn>
            <FadeIn delay={0.3} className="text-center" data-id="element-712">
              <div className="w-16 h-16 bg-gold rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6 shadow-md" data-id="element-713">
                <CheckCircleIcon className="w-8 h-8" data-id="element-714" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-715">
                Snel opgelost
              </h3>
              <p className="text-gray-600" data-id="element-716">
                Ik zorg voor een snelle uitkering of reparatie, zodat jij weer
                verder kunt.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Insurance Grid */}
      <section className="py-20 md:py-28 bg-warm" data-id="element-717">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-718">
          <SectionHeading title="Onze Verzekeringen" data-id="element-719" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-720">
            <FadeIn delay={0.1} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-721">
              <div className="w-12 h-12 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-722">
                <HomeIcon className="w-6 h-6 text-navy" data-id="element-723" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-724">Wonen</h3>
              <p className="text-gray-600" data-id="element-725">
                Opstal- en inboedelverzekering perfect afgestemd op jouw
                (nieuwe) woning of woonboot.
              </p>
            </FadeIn>

            <FadeIn delay={0.2} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-726">
              <div className="w-12 h-12 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-727">
                <HeartPulseIcon className="w-6 h-6 text-navy" data-id="element-728" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-729">
                Overlijdensrisico
              </h3>
              <p className="text-gray-600" data-id="element-730">
                Financiële rust voor je nabestaanden. Zorg dat de hypotheek
                betaalbaar blijft als jij of je partner wegvalt.
              </p>
            </FadeIn>

            <FadeIn delay={0.3} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-731">
              <div className="w-12 h-12 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-732">
                <BriefcaseIcon className="w-6 h-6 text-navy" data-id="element-733" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-734">
                Inkomensbescherming
              </h3>
              <p className="text-gray-600" data-id="element-735">
                Vang de financiële klap op bij arbeidsongeschiktheid of
                werkloosheid. Cruciaal voor ondernemers en huiseigenaren.
              </p>
            </FadeIn>
          </div>

          <FadeIn delay={0.6} className="mt-12 text-center" data-id="element-736">
            <p className="text-gray-500 text-sm max-w-2xl mx-auto" data-id="element-737">
              Ik adviseer alleen over verzekeringen die direct relevant zijn
              voor jouw hypotheek en financiële situatie. Geen onnodige polissen
              — alleen wat je écht nodig hebt.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Trust Quote */}
      <section className="py-16 bg-white" data-id="element-738">
        <div className="max-w-3xl mx-auto px-4 md:px-8" data-id="element-739">
          <FadeIn className="bg-warm p-8 md:p-10 rounded-2xl border border-gray-100 text-center" data-id="element-740">
            <div className="flex justify-center gap-1 mb-4" data-id="element-741">
              <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-742" />
              <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-743" />
              <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-744" />
              <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-745" />
              <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-746" />
            </div>
            <p className="text-gray-700 italic text-lg leading-relaxed mb-4" data-id="element-747">
              "Na een waterschade was alles binnen een week geregeld. Eén
              telefoontje en Jordy nam het volledig over. Dat is pas service."
            </p>
            <p className="font-bold text-navy" data-id="element-748">Familie Bakker</p>
          </FadeIn>
        </div>
      </section>

      {/* Cross-page CTA */}
      <CTASection title="Op zoek naar hypotheekadvies?" subtitle="Onze verzekeringen sluiten naadloos aan op jouw hypotheek. Bekijk ook ons hypotheekadvies." buttonText="Bekijk hypotheekadvies" buttonVariant="secondary" onNavigate={onNavigate} page="hypotheken" variant="light" data-id="element-749" />
    </div>;
}